import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

const REFERRAL_LS_KEY = 'dxz_ref_code';
const REFERRAL_EXPIRY_KEY = 'dxz_ref_expiry';
const REFERRAL_EXPIRY_DAYS = 7;

const EnquiryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    enquiryType: 'general'
  });

  // On mount: read ?ref= from URL and persist to localStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refFromUrl = params.get('ref');

    if (refFromUrl) {
      // Save to localStorage with 7-day expiry
      const expiry = Date.now() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(REFERRAL_LS_KEY, refFromUrl.toUpperCase());
      localStorage.setItem(REFERRAL_EXPIRY_KEY, expiry.toString());
      setReferralCode(refFromUrl.toUpperCase());
    } else {
      // Read from localStorage if not expired
      const saved = localStorage.getItem(REFERRAL_LS_KEY);
      const expiry = localStorage.getItem(REFERRAL_EXPIRY_KEY);
      if (saved && expiry && Date.now() < parseInt(expiry)) {
        setReferralCode(saved);
      }
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.message) {
      toast.error('Please fill in name and message');
      return;
    }

    setLoading(true);
    try {
      await api.post('/enquiry/demo', {
        ...formData,
        referralCode: referralCode || undefined
      });
      toast.success('Enquiry submitted successfully! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        enquiryType: 'general'
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit enquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Contact Us
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Have questions about our healthcare services? Get in touch with us.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Referral Banner */}
          {referralCode && (
            <div className="flex items-center gap-3 bg-[#143E78]/8 border border-[#143E78]/20 rounded-xl px-4 py-3">
              <div className="w-7 h-7 bg-[#143E78] rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-black text-[#143E78] uppercase tracking-widest">Referred by a Partner</p>
                <p className="text-[11px] text-gray-500 font-bold">Code: <span className="font-black text-[#143E78]">{referralCode}</span></p>
              </div>
            </div>
          )}
          <div className="bg-white py-8 px-6 shadow rounded-lg space-y-4">
            <div>
              <label htmlFor="enquiryType" className="block text-sm font-medium text-gray-700">
                Enquiry Type
              </label>
              <select
                id="enquiryType"
                name="enquiryType"
                value={formData.enquiryType}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="general">General Enquiry</option>
                <option value="appointment">Book Appointment</option>
                <option value="surgery">Surgery Information</option>
                <option value="billing">Billing & Payments</option>
                <option value="support">Technical Support</option>
              </select>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your email address"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                required
                value={formData.message}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Please describe your enquiry in detail..."
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Enquiry'}
              </button>
            </div>
          </div>
        </form>

        {/* Contact Information */}
        <div className="bg-white py-6 px-6 shadow rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-center">
              <span className="font-medium w-20">Phone:</span>
              <span>+1 (555) 123-4567</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-20">Email:</span>
              <span>info@healthcare-system.com</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-20">Address:</span>
              <span>123 Healthcare Ave, Medical City, MC 12345</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-20">Hours:</span>
              <span>Mon-Fri 9AM-6PM, Sat 9AM-2PM</span>
            </div>
          </div>
        </div>

        {/* Emergency Notice */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Emergency Services
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  For medical emergencies, please call emergency services immediately at 911 or visit the nearest emergency room.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryForm;